import {
  CompanySettings,
  ProInvoice,
  ProJob,
  Quote,
  TimeEntry,
} from '../types/pro'
import { daysSince, hoursOnJob, quoteTotal, startOfWeek } from './pro'
import { invoiceAmounts } from './invoice'

/** Work that has been done but not yet turned into an invoice. */
export interface UnbilledJob {
  job: ProJob
  hours: number
  labour: number
  materials: number
  value: number
}

function rateFor(job: ProJob, company: CompanySettings): number {
  return typeof job.hourlyRate === 'number' ? job.hourlyRate : company.hourlyRate
}

/** Only billable hours can be turned into money. */
function billableHoursOnJob(entries: TimeEntry[], jobId: string): number {
  return entries
    .filter((entry) => entry.jobId === jobId && entry.billable)
    .reduce((sum, entry) => sum + entry.hours, 0)
}

export function materialsValue(job: ProJob): number {
  return job.materials.reduce(
    (sum, material) => sum + material.quantity * material.salesPrice,
    0,
  )
}

/**
 * The single most useful number for a small trades company: money already
 * earned that nobody has asked for yet. Anything not yet invoiced counts,
 * whether the job is finished or still running.
 */
export function unbilledJobs(
  jobs: ProJob[],
  timeEntries: TimeEntry[],
  company: CompanySettings,
): UnbilledJob[] {
  return jobs
    .filter((job) => job.status !== 'faktureret' && !job.invoiceId)
    .map((job) => {
      const hours = billableHoursOnJob(timeEntries, job.id ?? '')
      const labour = hours * rateFor(job, company)
      const materials = materialsValue(job)
      // A fixed-price job is worth the agreed sum, however long it took.
      const value =
        job.billingType === 'fast-pris'
          ? (job.fixedPrice ?? 0)
          : labour + materials
      return { job, hours, labour, materials, value }
    })
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value)
}

export interface MoneyPicture {
  /** Earned but not invoiced. */
  unbilled: number
  unbilledJobCount: number
  /** Invoiced and waiting to be paid, excluding overdue. */
  outstanding: number
  /** Past the due date. */
  overdue: number
  overdueCount: number
  /** Paid this month. */
  paidThisMonth: number
}

export function moneyPicture(
  jobs: ProJob[],
  invoices: ProInvoice[],
  timeEntries: TimeEntry[],
  company: CompanySettings,
): MoneyPicture {
  const unbilledList = unbilledJobs(jobs, timeEntries, company)
  const now = new Date()

  let outstanding = 0
  let overdue = 0
  let overdueCount = 0
  let paidThisMonth = 0

  invoices.forEach((invoice) => {
    const sums = invoiceAmounts(invoice)
    if (invoice.status === 'forfalden') {
      overdue += sums.outstanding
      overdueCount += 1
    } else if (['sendt', 'godkendt'].includes(invoice.status)) {
      outstanding += sums.outstanding
    }

    if (invoice.status === 'betalt') {
      const date = new Date(invoice.invoiceDate)
      if (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        paidThisMonth += sums.total
      }
    }
  })

  return {
    unbilled: unbilledList.reduce((sum, entry) => sum + entry.value, 0),
    unbilledJobCount: unbilledList.length,
    outstanding,
    overdue,
    overdueCount,
    paidThisMonth,
  }
}

export interface WeekHours {
  total: number
  billable: number
  /** Hours per weekday, Monday first. */
  perDay: number[]
}

export function weekHours(timeEntries: TimeEntry[], today = new Date()): WeekHours {
  const monday = startOfWeek(today)
  const perDay = [0, 0, 0, 0, 0, 0, 0]
  let total = 0
  let billable = 0

  timeEntries.forEach((entry) => {
    const date = new Date(entry.date)
    const index = Math.floor(
      (date.getTime() - monday.getTime()) / (1000 * 60 * 60 * 24),
    )
    if (index < 0 || index > 6) return
    perDay[index] += entry.hours
    total += entry.hours
    if (entry.billable) billable += entry.hours
  })

  return { total, billable, perDay }
}

export type ActionKind =
  | 'faktura-mangler'
  | 'forfalden'
  | 'tilbud-uden-svar'
  | 'igang-uden-timer'
  | 'ikke-planlagt'

export interface ActionItem {
  id: string
  kind: ActionKind
  title: string
  detail: string
  /** Shown on the right, e.g. an amount or an age. */
  meta: string
  urgent: boolean
  /** What tapping the row should open. */
  target:
    | { kind: 'job'; id: string }
    | { kind: 'invoice'; id: string }
    | { kind: 'quote'; id: string }
}

const kr = (value: number) =>
  `${Math.round(value).toLocaleString('da-DK')} kr.`

/**
 * The dashboard's to-do list, derived entirely from the data rather than
 * asked for. Ordered so the most expensive problem sits at the top.
 */
export function actionItems(
  jobs: ProJob[],
  invoices: ProInvoice[],
  quotes: Quote[],
  timeEntries: TimeEntry[],
  company: CompanySettings,
): ActionItem[] {
  const items: ActionItem[] = []

  // Finished work nobody has billed.
  unbilledJobs(jobs, timeEntries, company)
    .filter((entry) => entry.job.status === 'faerdig')
    .forEach((entry) =>
      items.push({
        id: `bill-${entry.job.id}`,
        kind: 'faktura-mangler',
        title: `Fakturer ${entry.job.jobNo} — ${entry.job.title}`,
        detail: `${entry.job.customerName} · ${entry.hours.toLocaleString('da-DK')} t + materialer`,
        meta: kr(entry.value),
        urgent: true,
        target: { kind: 'job', id: entry.job.id ?? '' },
      }),
    )

  // Money someone owes you.
  invoices
    .filter((invoice) => invoice.status === 'forfalden')
    .forEach((invoice) =>
      items.push({
        id: `overdue-${invoice.id}`,
        kind: 'forfalden',
        title: `Faktura ${invoice.invoiceNo} er forfalden`,
        detail: `${invoice.customerName} · ${daysSince(invoice.dueDate)} dage over`,
        meta: kr(invoiceAmounts(invoice).outstanding),
        urgent: true,
        target: { kind: 'invoice', id: invoice.id ?? '' },
      }),
    )

  // A quote nobody answered is worth a phone call, not a second email.
  quotes
    .filter(
      (quote) =>
        ['sendt', 'set'].includes(quote.status) &&
        daysSince(quote.sentDate ?? quote.createdDate) >= 5,
    )
    .forEach((quote) =>
      items.push({
        id: `quote-${quote.id}`,
        kind: 'tilbud-uden-svar',
        title: `Følg op på tilbud ${quote.quoteNo}`,
        detail: `${quote.customerName} · sendt for ${daysSince(
          quote.sentDate ?? quote.createdDate,
        )} dage siden`,
        meta: kr(quoteTotal(quote, company.vatRate)),
        urgent: false,
        target: { kind: 'quote', id: quote.id ?? '' },
      }),
    )

  // Started but no hours booked — the classic way to lose a day's pay.
  jobs
    .filter(
      (job) =>
        job.status === 'i-gang' && hoursOnJob(timeEntries, job.id ?? '') === 0,
    )
    .forEach((job) =>
      items.push({
        id: `hours-${job.id}`,
        kind: 'igang-uden-timer',
        title: `Ingen timer på ${job.jobNo}`,
        detail: `${job.title} · ${job.customerName}`,
        meta: 'I gang',
        urgent: false,
        target: { kind: 'job', id: job.id ?? '' },
      }),
    )

  // Accepted work with no date yet.
  jobs
    .filter((job) => job.status === 'ikke-planlagt')
    .forEach((job) =>
      items.push({
        id: `plan-${job.id}`,
        kind: 'ikke-planlagt',
        title: `Planlæg ${job.jobNo} — ${job.title}`,
        detail: `${job.customerName} · ${job.city}`,
        meta: `${job.estimatedHours} t`,
        urgent: false,
        target: { kind: 'job', id: job.id ?? '' },
      }),
    )

  return items.sort((a, b) => Number(b.urgent) - Number(a.urgent))
}
